import React, { useState, useEffect } from 'react';
import axiosInstance from '../utils/axiosConfig';
import { getActionErrorMessage } from '../utils/errorHandler';
import "../styles/ViewAdminSchedules.css";
import "../styles/PerceptorAvailabilityForm.css";
import LoadingSpinner from './LoadingSpinner';
import { MATCH_BY_INSTITUTION, getCurriculumMatchBy } from '../utils/rankUtils';

const PreceptorAvailabilityForm = () => {
    const [curriculaWithConfigs, setCurriculaWithConfigs] = useState([]);
    const [selectedCurriculumId, setSelectedCurriculumId] = useState('');
    // Shape: { curr_id: { entity_id: [slots] } } — entity_id is a speciality id when
    // the curriculum's rank matches by experience, an institution id otherwise.
    const [rotationsAvailabilities, setRotationsAvailabilities] = useState({});
    const [preceptorSpecialityIds, setPreceptorSpecialityIds] = useState([]);
    const [preceptorInstitutionIds, setPreceptorInstitutionIds] = useState([]);
    const [specialities, setSpecialities] = useState([]);
    const [institutions, setInstitutions] = useState([]);
    const [ranks, setRanks] = useState([]);
    const [selectedEntityId, setSelectedEntityId] = useState('');
    const [savingKey, setSavingKey] = useState(null); // `${currId}_${specId}`
    const [fetchLoading, setFetchLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [noCurriculaFound, setNoCurriculaFound] = useState(false);

    const getRotationCount = (config) => {
        const fromRotations = Array.isArray(config?.rotations) ? config.rotations.length : 0;
        const fromCount = parseInt(config?.rotationsCount, 10);
        const fromNumber = parseInt(config?.rotationsNumber, 10);
        return Math.max(1, fromRotations || fromCount || fromNumber || 8);
    };

    const normalizeAvailability = (list, count) => {
        const safe = Array.isArray(list) ? [...list] : [];
        const normalized = Array.from({ length: count }, (_, index) => {
            const raw = index < safe.length ? safe[index] : 0;
            const parsed = parseInt(raw, 10);
            return Math.max(0, Math.min(2, Number.isNaN(parsed) ? 0 : parsed));
        });
        return normalized;
    };

    const extractCurriculumIds = (preceptorData) => {
        const ids = new Set();
        const addId = (value) => {
            if (value == null) return;
            const id = typeof value === 'object'
                ? (value._id ?? value.id ?? value.curriculum_id ?? value.curriculumId)
                : value;
            if (id != null && String(id).trim()) ids.add(String(id));
        };

        if (Array.isArray(preceptorData?.curriculum_ids)) preceptorData.curriculum_ids.forEach(addId);
        if (Array.isArray(preceptorData?.currentCurriculums)) preceptorData.currentCurriculums.forEach(addId);
        if (Array.isArray(preceptorData?.curriculums)) preceptorData.curriculums.forEach(addId);
        addId(preceptorData?.currentCurriculum);
        addId(preceptorData?.curriculum_id);
        addId(preceptorData?.curriculumId);
        return Array.from(ids);
    };

    useEffect(() => {
        const fetchData = async () => {
            setFetchLoading(true);
            setNoCurriculaFound(false);
            setError('');
            try {
                const dashboardRes = await axiosInstance.get('/auth/preceptor/dashboard');
                const preceptorData = dashboardRes.data?.data || dashboardRes.data;
                const curriculumIds = extractCurriculumIds(preceptorData);

                // Resolve preceptor's speciality and institution IDs — a preceptor may
                // hold several of each; which list applies depends on each curriculum's rank.
                const rawSpecIds = preceptorData?.specialityIds;
                const specIds = Array.isArray(rawSpecIds) && rawSpecIds.length > 0
                    ? rawSpecIds.map(String)
                    : preceptorData?.specialityId ? [String(preceptorData.specialityId)] : [];
                setPreceptorSpecialityIds(specIds);
                if (specIds.length > 0) setSelectedEntityId((prev) => prev || specIds[0]);

                const rawInstIds = preceptorData?.institutionIds;
                const instIds = Array.isArray(rawInstIds) && rawInstIds.length > 0
                    ? rawInstIds.map(String)
                    : preceptorData?.institutionId ? [String(preceptorData.institutionId)] : [];
                setPreceptorInstitutionIds(instIds);

                if (!curriculumIds.length) {
                    setNoCurriculaFound(true);
                    return;
                }

                const [curriculumListRes, specialitiesRes, institutionsRes, ranksRes, ...configResults] = await Promise.all([
                    axiosInstance.get('/curriculum/curriculums').catch(() => ({ data: [] })),
                    axiosInstance.get('/crud/get_all_specialities').catch(() => ({ data: [] })),
                    axiosInstance.get('/crud/get_all_institutions').catch(() => ({ data: [] })),
                    axiosInstance.get('/ranks/ranks').catch(() => ({ data: [] })),
                    ...curriculumIds.map((currId) =>
                        axiosInstance.get(`/config/get_configuration_by_curriculum/${currId}`)
                            .then((res) => ({ currId, config: Array.isArray(res.data) ? res.data[0] : res.data }))
                            .catch(() => ({ currId, config: null }))
                    )
                ]);

                const curriculumById = {};
                (Array.isArray(curriculumListRes.data) ? curriculumListRes.data : []).forEach((c) => {
                    curriculumById[c._id] = c;
                });

                const rankList = Array.isArray(ranksRes?.data) ? ranksRes.data : [];
                setRanks(rankList);

                const specRaw = Array.isArray(specialitiesRes?.data) ? specialitiesRes.data : [];
                setSpecialities(specRaw.map((s) => ({ _id: String(s._id ?? s.id), name: s.name || String(s._id) })));

                const instRaw = Array.isArray(institutionsRes?.data) ? institutionsRes.data : [];
                setInstitutions(instRaw.map((i) => ({ _id: String(i._id ?? i.id), name: i.name || String(i._id) })));

                const defaultConfig = { rotations: [], rotationsNumber: 8, rotationsCount: 8 };
                const curricula = configResults.map((r) => ({
                    _id: r.currId,
                    name: curriculumById[r.currId]?.name || r.currId,
                    matchBy: getCurriculumMatchBy(rankList, curriculumById[r.currId]),
                    config: r.config || defaultConfig
                }));

                if (!curricula.length) {
                    setNoCurriculaFound(true);
                    return;
                }

                setCurriculaWithConfigs(curricula);
                setSelectedCurriculumId((prev) => prev || curricula[0]?._id || '');
                setSelectedEntityId((prev) => prev
                    || (curricula[0]?.matchBy === MATCH_BY_INSTITUTION ? instIds[0] : specIds[0])
                    || '');

                // Fetch availability: shape is { curr_id: { entity_id: [slots] } }
                const availRes = await axiosInstance.get('/crud/get_preceptor_availability');
                const availData = availRes.data || {};

                // Normalize: ensure each entity id under each curriculum has the right slot
                // length, using the entity list appropriate to that curriculum's matchBy.
                const updated = {};
                curricula.forEach((curr) => {
                    const rotCount = getRotationCount(curr.config);
                    const entityIds = curr.matchBy === MATCH_BY_INSTITUTION ? instIds : specIds;
                    const currLevel = availData[curr._id];
                    if (currLevel && typeof currLevel === 'object' && !Array.isArray(currLevel)) {
                        const normalized = {};
                        entityIds.forEach((eid) => {
                            normalized[eid] = normalizeAvailability(currLevel[eid], rotCount);
                        });
                        updated[curr._id] = normalized;
                    } else if (Array.isArray(currLevel)) {
                        // Old flat format: assign to the first entity
                        const normalized = {};
                        entityIds.forEach((eid, i) => {
                            normalized[eid] = i === 0 ? normalizeAvailability(currLevel, rotCount) : normalizeAvailability([], rotCount);
                        });
                        updated[curr._id] = normalized;
                    } else {
                        const normalized = {};
                        entityIds.forEach((eid) => { normalized[eid] = normalizeAvailability([], rotCount); });
                        updated[curr._id] = normalized;
                    }
                });
                setRotationsAvailabilities(updated);
            } catch (err) {
                setError(getActionErrorMessage(err, "fetch curriculum data"));
                setTimeout(() => setError(''), 5000);
            } finally {
                setFetchLoading(false);
            }
        };

        fetchData();
    }, []);

    const handleSubmit = async (curriculumId, entityId, availabilityArray, rotCount, matchBy) => {
        if (!entityId) {
            setError(matchBy === MATCH_BY_INSTITUTION ? 'No institution selected.' : 'No speciality selected.');
            return;
        }
        const key = `${curriculumId}_${entityId}`;
        setSavingKey(key);
        setError('');
        try {
            const normalizedAvailability = normalizeAvailability(availabilityArray, rotCount);
            await axiosInstance.put('/crud/preceptor/availability', null, {
                params: {
                    curriculum_id: curriculumId,
                    entity_id: entityId,
                    new_availability: JSON.stringify(normalizedAvailability)
                }
            });
            setRotationsAvailabilities((prev) => ({
                ...prev,
                [curriculumId]: {
                    ...(prev[curriculumId] || {}),
                    [entityId]: normalizedAvailability
                }
            }));
            const lookup = matchBy === MATCH_BY_INSTITUTION ? institutions : specialities;
            const entityName = lookup.find((item) => item._id === entityId)?.name || entityId;
            setSuccess(`Availability updated for ${entityName}`);
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            setError(getActionErrorMessage(err, "update availability"));
        } finally {
            setSavingKey(null);
        }
    };

    const handleAvailabilityChange = (curriculumId, entityId, rotationIndex, value) => {
        const newValue = Math.max(0, Math.min(2, parseInt(value, 10) || 0));
        setRotationsAvailabilities((prev) => {
            const currLevel = { ...(prev[curriculumId] || {}) };
            const entitySlots = [...(currLevel[entityId] || [])];
            entitySlots[rotationIndex] = newValue;
            currLevel[entityId] = entitySlots;
            return { ...prev, [curriculumId]: currLevel };
        });
    };

    const isWithinRegistration = (curriculum) => {
        const now = new Date();
        const regStart = new Date(curriculum.config?.form_registration?.startDate);
        const regEnd = new Date(curriculum.config?.form_registration?.endDate);
        const validDates = !isNaN(regStart.getTime()) && !isNaN(regEnd.getTime());
        return validDates && now >= regStart && now <= regEnd;
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const parsed = new Date(dateString);
        return isNaN(parsed.getTime()) ? '-' : parsed.toLocaleDateString();
    };

    const selectedCurriculum = curriculaWithConfigs.find((c) => c._id === selectedCurriculumId) || null;

    return (
        <div className="universal-white-container view-admin-schedules-page perceptor-availability-form">
            <div className="view-admin-schedules-header">
                <div className="view-admin-schedules-header-inner">
                    <div className="view-admin-schedules-header-left">
                        <div className="view-admin-schedules-header-icon">
                            <span aria-hidden>AV</span>
                        </div>
                        <div>
                            <h1 className="view-admin-schedules-title">My Availability</h1>
                            <p className="view-admin-schedules-subtitle">Set your availability for curricula rotations</p>
                        </div>
                    </div>
                </div>
            </div>

            {success && <div className="alert alert-success">{success}</div>}
            {error && <div className="alert alert-error">{error}</div>}

            <div className="availability-content">
                {fetchLoading ? (
                    <div className="loading-state">
                        <LoadingSpinner text="Loading your curricula..." variant="inline" />
                    </div>
                ) : noCurriculaFound ? (
                    <div className="closed-registration-message">
                        <div className="registration-status">
                            <h4>No Curricula Found</h4>
                            <p>You are not currently assigned to any curricula. Please contact your administrator to be added to a curriculum.</p>
                        </div>
                    </div>
                ) : (
                    <>
                        <div className="curricula-summary">
                            <h3>Your Curricula ({curriculaWithConfigs.length})</h3>
                            <p>Select a curriculum to view or update your availability. Closed periods are still available for review.</p>
                            <div style={{ marginTop: '0.9rem', maxWidth: '420px' }}>
                                <label htmlFor="curriculum-selector" style={{ display: 'block', marginBottom: '0.4rem', fontWeight: 600, color: '#1e40af' }}>
                                    Curriculum
                                </label>
                                <select
                                    id="curriculum-selector"
                                    value={selectedCurriculumId}
                                    onChange={(e) => setSelectedCurriculumId(e.target.value)}
                                    className="availability-input"
                                    style={{ maxWidth: '100%', width: '100%', textAlign: 'left' }}
                                >
                                    {curriculaWithConfigs.map((curr) => {
                                        const currOpen = isWithinRegistration(curr);
                                        return (
                                            <option key={curr._id} value={curr._id}>
                                                {curr.name} {currOpen ? '(Open)' : '(Closed)'}
                                            </option>
                                        );
                                    })}
                                </select>
                            </div>
                        </div>

                        {selectedCurriculum && (() => {
                            const curriculum = selectedCurriculum;
                            const regOpen = isWithinRegistration(curriculum);
                            const rotCount = getRotationCount(curriculum.config);
                            const rotations = curriculum.config?.rotations || [];
                            const rotationSlots = Array.from({ length: rotCount }, (_, i) => rotations[i] || null);
                            const currAvail = rotationsAvailabilities[curriculum._id] || {};
                            const isInstitutionMatch = curriculum.matchBy === MATCH_BY_INSTITUTION;
                            const entityIds = isInstitutionMatch ? preceptorInstitutionIds : preceptorSpecialityIds;
                            const entityLookup = isInstitutionMatch ? institutions : specialities;
                            const entityLabel = isInstitutionMatch ? 'Institution' : 'Rotation / Experience';
                            const activeEntityId = (entityIds.includes(selectedEntityId) ? selectedEntityId : entityIds[0]) || '';
                            const availability = normalizeAvailability(currAvail[activeEntityId], rotCount);
                            const saveKey = `${curriculum._id}_${activeEntityId}`;

                            return (
                                <div key={curriculum._id} className="curriculum-block">
                                    <div className="create-form-section">
                                        <div className="curriculum-head">
                                            <div>
                                                <h3 className="section-title" style={{ margin: 0 }}>{curriculum.name}</h3>
                                                <p className="curriculum-meta">
                                                    {rotCount} rotations • {regOpen ? 'Registration Open' : 'Registration Closed'}
                                                </p>
                                            </div>
                                            <div className={`registration-range ${regOpen ? 'open' : 'closed'}`}>
                                                <span className="registration-period-label">Registration Period</span>
                                                {curriculum.config?.form_registration ? (
                                                    <span>
                                                        {formatDate(curriculum.config.form_registration?.startDate)} - {formatDate(curriculum.config.form_registration?.endDate)}
                                                    </span>
                                                ) : 'No dates set'}
                                            </div>
                                        </div>

                                        {/* Entity selector — shown when the preceptor has more than one
                                            experience/institution for this curriculum's match type */}
                                        {entityIds.length > 1 && (
                                            <div style={{ marginBottom: '1.2rem', maxWidth: '360px' }}>
                                                <label htmlFor={`entity-selector-${curriculum._id}`} style={{ display: 'block', marginBottom: '0.4rem', fontWeight: 600, color: '#1e40af' }}>
                                                    {entityLabel}
                                                </label>
                                                <select
                                                    id={`entity-selector-${curriculum._id}`}
                                                    value={activeEntityId}
                                                    onChange={(e) => setSelectedEntityId(e.target.value)}
                                                    className="availability-input"
                                                    style={{ maxWidth: '100%', width: '100%' }}
                                                >
                                                    {entityIds.map((eid) => (
                                                        <option key={eid} value={eid}>
                                                            {entityLookup.find((item) => item._id === eid)?.name || eid}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        )}

                                        {regOpen ? (
                                            <>
                                                <div className="items-grid">
                                                    {rotationSlots.map((rotation, index) => {
                                                        const value = availability[index] ?? 0;
                                                        return (
                                                            <div key={`${curriculum._id}-${activeEntityId}-${index}`} className="modern-item-card availability-card">
                                                                <div className="item-preview">
                                                                    <div className="item-header">
                                                                        <div className="item-main-info">
                                                                            <h4 className="item-name">Rotation {index + 1}</h4>
                                                                            {rotation && (
                                                                                <div className="rotation-dates">
                                                                                    <span className="date-label">Period:</span>
                                                                                    <span className="date-value">
                                                                                        {formatDate(rotation.startDate)} - {formatDate(rotation.endDate)}
                                                                                    </span>
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                        <div className="availability-display">
                                                                            <span className={`availability-value ${value === 0 ? 'unavailable' : value === 1 ? 'single' : 'double'}`}>
                                                                                {value} student{value !== 1 ? 's' : ''}
                                                                            </span>
                                                                        </div>
                                                                    </div>

                                                                    <div className="availability-input-section">
                                                                        <label htmlFor={`${curriculum._id}-${activeEntityId}-rot-${index}`} className="input-label">
                                                                            Update Availability:
                                                                        </label>
                                                                        <div className="input-group">
                                                                            <input
                                                                                id={`${curriculum._id}-${activeEntityId}-rot-${index}`}
                                                                                type="number"
                                                                                min="0"
                                                                                max="2"
                                                                                value={value}
                                                                                onChange={(e) => handleAvailabilityChange(curriculum._id, activeEntityId, index, e.target.value)}
                                                                                className="availability-input"
                                                                                disabled={!regOpen}
                                                                            />
                                                                            <div className="input-helper">
                                                                                <span className="helper-text">0-2 students</span>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>

                                                <button
                                                    type="button"
                                                    onClick={() => handleSubmit(curriculum._id, activeEntityId, availability, rotCount, curriculum.matchBy)}
                                                    disabled={savingKey === saveKey || !activeEntityId}
                                                    className="submit-btn"
                                                    style={{ marginTop: '1.5rem' }}
                                                >
                                                    {savingKey === saveKey
                                                        ? 'Saving...'
                                                        : `Save Availability for ${curriculum.name}${entityIds.length > 1 ? ` — ${entityLookup.find((item) => item._id === activeEntityId)?.name || activeEntityId}` : ''}`}
                                                </button>
                                            </>
                                        ) : (
                                            <div className="registration-closed-card">
                                                <div className="registration-closed-label">Locked</div>
                                                <p style={{ margin: 0, fontWeight: 500, color: '#78350f' }}>
                                                    Registration for {curriculum.name} is currently closed
                                                </p>
                                                <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.9rem', color: '#92400e' }}>
                                                    Opens {formatDate(curriculum.config?.form_registration?.startDate)} and closes {formatDate(curriculum.config?.form_registration?.endDate)}
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })()}
                    </>
                )}
            </div>
        </div>
    );
};

export default PreceptorAvailabilityForm;
